import { Controller } from "@hotwired/stimulus"

// Ticks off password rules as the member types.
//
// The rules themselves are rendered server-side from the Devise configuration
// (see PasswordRequirementsHelper), so without JavaScript the member still sees
// the full list -- this controller only marks each one met or unmet. That also
// means the list cannot drift out of step with the validator: nothing here
// knows what the rules are, only how to test the ones it was handed.
//
// The patterns mirror DeviseSecurity::PasswordComplexityValidator#patterns.
// Ruby's \p{Digit} is Unicode Nd, and \p{Lower}/\p{Upper} are the Lowercase and
// Uppercase binary properties; the JavaScript spellings below were checked
// against the Ruby ones character by character, including Greek, Arabic-Indic
// and Devanagari digits, CJK, emoji and currency symbols.
//
// Counting with matchAll rather than test() is deliberate: the validator
// compares `password.scan(pattern).size` against a minimum, so a rule asking
// for two digits stays satisfiable here if the config ever asks for one.
const PATTERNS = {
  lower: /\p{Lowercase}/gu,
  upper: /\p{Uppercase}/gu,
  digit: /\p{Nd}/gu,
  symbol: /\p{P}|\p{S}/gu
}

export default class extends Controller {
  static targets = ["password", "confirmation", "email", "rule", "announcement"]

  connect() {
    // A restored form or an autofilled password should not start out marked
    // unmet, so evaluate whatever is already there before the first keystroke.
    this.refresh()
  }

  refresh() {
    const password = this.hasPasswordTarget ? this.passwordTarget.value : ""

    // Before the member has typed anything every rule is merely outstanding,
    // not failed. Opening the form to a column of red crosses reads as a list
    // of complaints about something they have not done yet.
    const started = password.length > 0

    let met = 0
    this.ruleTargets.forEach((rule) => {
      const satisfied = this.satisfies(rule, password)
      if (satisfied) met += 1
      this.mark(rule, started ? satisfied : null)
    })

    this.announce(started, met, this.ruleTargets.length)
  }

  satisfies(rule, password) {
    switch (rule.dataset.rule) {
      case "length": {
        const min = Number(rule.dataset.min)
        const max = Number(rule.dataset.max)

        // Code points, not UTF-16 units. String#length in Ruby counts
        // characters, so "µ😀!aÉ५一" is 7 to the validator and 8 to a bare
        // JavaScript .length -- the checklist would have called that long
        // enough and the server would still have rejected it, which is the
        // exact loop this list exists to prevent.
        const length = [...password].length
        return length >= min && length <= max
      }
      case "match": {
        const confirmation = this.hasConfirmationTarget ? this.confirmationTarget.value : ""
        return password.length > 0 && password === confirmation
      }
      case "email": {
        // Devise.secure_compare(password.downcase.strip, email.downcase.strip)
        const email = this.hasEmailTarget ? this.emailTarget.value : ""
        if (!email.trim()) return true
        return password.trim().toLowerCase() !== email.trim().toLowerCase()
      }
      default: {
        const pattern = PATTERNS[rule.dataset.rule]
        if (!pattern) return true

        const count = Number(rule.dataset.count || 1)
        return [...password.matchAll(pattern)].length >= count
      }
    }
  }

  // null means "not yet assessed" -- see refresh.
  mark(rule, satisfied) {
    rule.classList.toggle("text-success", satisfied === true)
    rule.classList.toggle("text-body-secondary", satisfied !== true)

    const icon = rule.querySelector("[data-password-checklist-icon]")
    if (icon) {
      icon.className = satisfied === true
        ? "fa-solid fa-circle-check me-2"
        : "fa-regular fa-circle me-2"
    }

    // The list is the password field's accessible description, so each item
    // has to carry its own state in text -- a colour and an icon say nothing
    // to a screen reader.
    const state = rule.querySelector("[data-password-checklist-state]")
    if (state) {
      state.textContent = satisfied === true ? " (met)" : ""
    }
  }

  // One debounced summary rather than an interruption per keystroke: announcing
  // each rule as it flips would talk over the member while they are still
  // typing the password.
  announce(started, met, total) {
    if (!this.hasAnnouncementTarget) return

    clearTimeout(this.announceTimer)
    this.announceTimer = setTimeout(() => {
      this.announcementTarget.textContent = started
        ? `${met} of ${total} password requirements met`
        : ""
    }, 700)
  }

  disconnect() {
    clearTimeout(this.announceTimer)
  }
}
